import './components/modern-dashboard.css';
import './components/dark-theme-override.css';
import './components/light-theme.css';
import './components/booking-calendar.css';
import './components/booking-components.css';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';  /* <-- NEW */
import { HelpdeskLanguageProvider } from './context/HelpdeskLanguageContext';
import Dashboard from './pages/Dashboard';
import Login from './components/Login';
import DemoLogin from './pages/DemoLogin';
import ResetPassword from './components/ResetPassword';
import Registration from './pages/Registration';
import Queue from './pages/Queue';
import MyAssignments from './pages/MyAssignments';
import CannedResponses from './pages/CannedResponses';
import MyConversations from './pages/MyConversations';
import Agents from './pages/Agents';
import Teams from './pages/Teams';
import Analytics from './pages/Analytics';
import AllConversations from './pages/AllConversations';
import NegativeFeedback from './pages/NegativeFeedback';
import LeadsPage from './pages/LeadsPage';
import HelpdeskDashboard from './pages/helpdesk/HelpdeskDashboard';
import HelpdeskTickets from './pages/helpdesk/HelpdeskTickets';
import HelpdeskTicketDetail from './pages/helpdesk/HelpdeskTicketDetail';
import HelpdeskCreateTicket from './pages/helpdesk/HelpdeskCreateTicket';
import HelpdeskDepartments from './pages/helpdesk/HelpdeskDepartments';
import HelpdeskClients from './pages/helpdesk/HelpdeskClients';
import Marketing from './pages/Marketing';
import Organizations from './components/Organizations';
import WhatsAppAudienceEditor from './components/WhatsAppAudienceEditor';
import MarketingContactList from './pages/MarketingContactList';
import MaintenancePlans from './pages/fsm/MaintenancePlans';
import PreventiveSchedule from './pages/fsm/PreventiveSchedule';
import FSMSettings from './pages/fsm/FSMSettings';
// VSM (Vehicle Service Management) Pages
import VSMClients from './pages/vsm/VSMClients';
import VSMVehicles from './pages/vsm/VSMVehicles';
import VSMServiceTypes from './pages/vsm/VSMServiceTypes';
import VSMContracts from './pages/vsm/VSMContracts';
import VSMContractForm from './pages/vsm/VSMContractForm';
import VSMContractDetail from './pages/vsm/VSMContractDetail';
import VSMHolidays from './pages/vsm/VSMHolidays';
import VSMSchedule from './pages/vsm/VSMSchedule';
import VSMCapture from './pages/vsm/VSMCapture';

// Booking System Imports
import BookingCalendar from './pages/BookingCalendar';
import BookingsList from './pages/BookingsList';
import EventTypes from './pages/EventTypes';
import AgentProfile from './pages/AgentProfile';
import Availability from './pages/Availability';
import PublicBooking from './pages/PublicBooking';

// FSM (Field Service Management) Pages
import EquipmentList from './pages/fsm/EquipmentList';
import EquipmentDetail from './pages/fsm/EquipmentDetail';
import EquipmentForm from './pages/fsm/EquipmentForm';
import FSMClients from './pages/fsm/FSMClients';
import EquipmentCategories from './pages/fsm/EquipmentCategories';
import ContractList from "./pages/fsm/ContractList";
import ContractForm from "./pages/fsm/ContractForm";
import ContractDetail from "./pages/fsm/ContractDetail";
import PriceBooks from "./pages/fsm/PriceBooks";
import BusinessHours from "./pages/fsm/BusinessHours";
import ContractTemplates from './pages/fsm/ContractTemplates';
import ContractTemplateBuilder from './pages/fsm/ContractTemplateBuilder';
import WOTemplates from './pages/fsm/WOTemplates';
import WOTemplateBuilder from './pages/fsm/WOTemplateBuilder';
import PartsCatalog from './pages/fsm/PartsCatalog';
import PublicEquipmentPage from './pages/fsm/PublicEquipmentPage';
import FSMDashboard from './pages/fsm/FSMDashboard';
import WorkOrderList from './pages/fsm/WorkOrderList';
import WorkOrderForm from './pages/fsm/WorkOrderForm';
import WorkOrderDetail from './pages/fsm/WorkOrderDetail';
import Technicians from './pages/fsm/Technicians';
import DispatchBoard from './pages/fsm/DispatchBoard';
import FSMReports from './pages/fsm/FSMReports';
import FSMReportRunner from './pages/fsm/FSMReportRunner';



// Token expiration check utility
const isTokenExpired = () => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return true;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const decoded = JSON.parse(jsonPayload);
    const currentTime = Date.now() / 1000;
    
    return decoded.exp < (currentTime - 60);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};


const checkBackendConnection = async () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    const response = await fetch('/api/v1/auth/me', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.status === 401 || response.status === 403) {
      console.log('Token invalid - server rejected');
      return false;
    }
    
    return true;
  } catch (error) {
    console.log('Backend connection check failed (will retry):', error.message);
    return true;
  }
};

// ============================================
// SLIDING SESSION — agents stay signed in (Mohamed, 2026-10-12)
// ============================================
// The token lasts 30 days, and POST /auth/renew swaps it for a fresh 30-day
// one. Renewing on load, every 6 hours and whenever the tab comes back into
// view means an agent who opens the app at least once a month is never sent
// to the login page. Only a 401 from /renew (deactivated account, or a token
// already dead) signs out; a network or server error keeps the current token.
const RENEW_EVERY_MS = 6 * 60 * 60 * 1000;
let lastRenewAt = 0;

const renewSession = async () => {
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired()) return;
  if (Date.now() - lastRenewAt < 60 * 1000) return; // focus + interval landing together
  lastRenewAt = Date.now();
  try {
    const response = await fetch('/api/v1/auth/renew', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.status === 401) {
      clearAuthAndRedirect();
      return;
    }
    if (!response.ok) return;
    const data = await response.json();
    // Only replace the token we renewed — not one a fresh login wrote meanwhile.
    if (data.token && localStorage.getItem('token') === token) {
      localStorage.setItem('token', data.token);
    }
  } catch (error) {
    console.log('Session renewal failed (will retry):', error.message);
  }
};

// ============================================
// AUTO-ONLINE & HEARTBEAT SYSTEM
// ============================================
const setAgentOnline = async () => {
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired()) return;
  
  try {
    await fetch('/api/v1/agents/me/status', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ is_online: true })
    });
    console.log('🟢 Agent auto-set to ONLINE (page load/refresh)');
    
    await fetch('/api/v1/agents/me/heartbeat', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error('Failed to auto-set online:', error.message);
  }
};

const sendHeartbeat = async () => {
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired()) return;
  
  try {
    await fetch('/api/v1/agents/me/heartbeat', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('💓 Heartbeat sent');
  } catch (error) {
    console.error('Heartbeat failed:', error.message);
  }
};

function App() {
  const [isChecking, setIsChecking] = useState(true);

  // Sliding session: renew now, every 6 hours, and when the tab is shown again.
  useEffect(() => {
    renewSession();
    const renewInterval = setInterval(renewSession, RENEW_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRenewAt > RENEW_EVERY_MS) renewSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(renewInterval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired()) {
      setAgentOnline();
      
      const heartbeatInterval = setInterval(() => {
        sendHeartbeat();
      }, 5 * 60 * 1000);
      
      return () => {
        clearInterval(heartbeatInterval);
      };
    }
  }, []);

  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsChecking(false);
        return;
      }
      
      if (isTokenExpired()) {
        console.log('Token expired on app load - redirecting to login');
        clearAuthAndRedirect();
        return;
      }
      
      setIsChecking(false);
    };

    checkTokenValidity();

    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      if (isTokenExpired()) {
        console.log('Token expired - redirecting to login');
        clearAuthAndRedirect();
      }
    }, 60000);

    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    if (isTokenExpired()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
    return true;
  };

  // Get user's first available route based on their menu_permissions.
  // Used for fallback redirects when a user lacks permission for a specific page.
  const getFirstAvailableRoute = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const perms = Array.isArray(user.menu_permissions) ? user.menu_permissions : [];
      // Map permission key -> route. Order matters (priority).
      const map = [
        ['dashboard',         '/dashboard'],
        ['my_assignments',    '/my-assignments'],
        ['queue',             '/queue'],
        ['all_conversations', '/all-conversations'],
        ['helpdesk',          '/helpdesk'],
        ['leads',             '/leads'],
        ['marketing',         '/marketing'],
        ['fsm_dashboard',     '/fsm/dashboard'],
        ['fsm_work_orders',   '/fsm/work-orders'],
        ['fsm_equipment',     '/fsm/equipment'],
        ['vsm_clients',       '/vsm/clients'],
        ['vsm_schedule',      '/vsm/schedule'],
        ['my_bookings',       '/bookings'],
        ['booking_calendar',  '/booking-calendar'],
        ['analytics',         '/analytics'],
        ['agents',            '/agents'],
        ['my_profile',        '/agent-profile'],
      ];
      for (const [perm, route] of map) {
        if (perms.includes(perm)) return route;
      }
      // Last-resort fallback
      return '/agent-profile';
    } catch {
      return '/login';
    }
  };

  // Permission-aware route guard.
  // Usage: <ProtectedRoute>...</ProtectedRoute>                      (auth-only)
  //        <ProtectedRoute permission="dashboard">...</ProtectedRoute> (auth + permission)
  const ProtectedRoute = ({ children, permission }) => {
    const location = useLocation();
    if (!isAuthenticated()) {
      return <Navigate to="/login" state={{ from: location.pathname }} />;
    }
    if (permission) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Admin without explicit permissions array gets full access (legacy behavior)
      if (user.role === 'admin' && !user.menu_permissions) return children;
      const perms = Array.isArray(user.menu_permissions) ? user.menu_permissions : [];
      if (!perms.includes(permission)) {
        // Redirect to first available page instead of showing forbidden screen
        return <Navigate to={getFirstAvailableRoute()} replace />;
      }
    }
    return children;
  };

  if (isChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a2e'
      }}>
        <div style={{ color: '#fff', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <HelpdeskLanguageProvider>
          <Router>
            <div className="App">
              <Routes>
          <Route path="/fsm/dashboard" element={<FSMDashboard />} />
          <Route path="/equipment/:qrCode" element={<PublicEquipmentPage />} />
                <Route path="/book/:agentSlug/:eventSlug" element={<PublicBooking />} />
                <Route path="/demo" element={<DemoLogin />} />
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Registration />} />
                <Route path="/dashboard" element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
                <Route path="/queue" element={<ProtectedRoute permission="queue"><Queue /></ProtectedRoute>} />
                <Route path="/my-assignments" element={<ProtectedRoute permission="my_assignments"><MyAssignments /></ProtectedRoute>} />
                <Route path="/my-conversations" element={<ProtectedRoute><MyConversations /></ProtectedRoute>} />
                <Route path="/canned-responses" element={<ProtectedRoute permission="canned_responses"><CannedResponses /></ProtectedRoute>} />
                <Route path="/agents" element={<ProtectedRoute permission="agents"><Agents /></ProtectedRoute>} />
                <Route path="/teams" element={<ProtectedRoute permission="teams"><Teams /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute permission="analytics"><Analytics /></ProtectedRoute>} />
                <Route path="/all-conversations" element={<ProtectedRoute permission="all_conversations"><AllConversations /></ProtectedRoute>} />
                <Route path="/organizations" element={<ProtectedRoute><Organizations /></ProtectedRoute>} />
                <Route path="/marketing" element={<ProtectedRoute permission="marketing"><Marketing /></ProtectedRoute>} />
                <Route path="/conversation/:id" element={<ProtectedRoute></ProtectedRoute>} />
                <Route path="/negative-feedback" element={<ProtectedRoute permission="negative_feedback"><NegativeFeedback /></ProtectedRoute>} />
                <Route path="/leads" element={<ProtectedRoute permission="leads"><LeadsPage /></ProtectedRoute>} />
                {/* Booking System Routes */}
                <Route path="/bookings" element={<ProtectedRoute permission="my_bookings"><BookingsList /></ProtectedRoute>} />
                <Route path="/booking-calendar" element={<ProtectedRoute permission="booking_calendar"><BookingCalendar /></ProtectedRoute>} />
                <Route path="/event-types" element={<ProtectedRoute permission="event_types"><EventTypes /></ProtectedRoute>} />
                <Route path="/availability" element={<ProtectedRoute permission="availability"><Availability /></ProtectedRoute>} />
                <Route path="/agent-profile" element={<ProtectedRoute permission="my_profile"><AgentProfile /></ProtectedRoute>} />
                {/* Helpdesk Routes */}
                <Route path="/helpdesk" element={<ProtectedRoute permission="helpdesk"><HelpdeskDashboard /></ProtectedRoute>} />
                <Route path="/helpdesk/tickets" element={<ProtectedRoute><HelpdeskTickets /></ProtectedRoute>} />
                <Route path="/helpdesk/tickets/new" element={<ProtectedRoute><HelpdeskCreateTicket /></ProtectedRoute>} />
                <Route path="/helpdesk/tickets/:id" element={<ProtectedRoute><HelpdeskTicketDetail /></ProtectedRoute>} />
                <Route path="/helpdesk/create-ticket" element={<ProtectedRoute><HelpdeskCreateTicket /></ProtectedRoute>} />
                <Route path="/helpdesk/clients" element={<ProtectedRoute permission="helpdesk_clients"><HelpdeskClients /></ProtectedRoute>} />
                <Route path="/helpdesk/departments" element={<ProtectedRoute permission="helpdesk_departments"><HelpdeskDepartments /></ProtectedRoute>} />
                                {/* FSM Routes */}
                <Route path="/fsm/equipment" element={<ProtectedRoute permission="fsm_equipment"><EquipmentList /></ProtectedRoute>} />
                <Route path="/fsm/equipment/new" element={<ProtectedRoute><EquipmentForm /></ProtectedRoute>} />
                <Route path="/fsm/equipment/:id" element={<ProtectedRoute><EquipmentDetail /></ProtectedRoute>} />
                <Route path="/fsm/equipment/:id/edit" element={<ProtectedRoute><EquipmentForm /></ProtectedRoute>} />
                <Route path="/fsm/clients" element={<ProtectedRoute permission="fsm_clients"><FSMClients /></ProtectedRoute>} />
                <Route path="/fsm/categories" element={<ProtectedRoute><EquipmentCategories /></ProtectedRoute>} />
                <Route path="/fsm/contracts" element={<ProtectedRoute permission="fsm_contracts"><ContractList /></ProtectedRoute>} />
                <Route path="/fsm/contracts/new" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />
                <Route path="/fsm/contracts/:id" element={<ProtectedRoute><ContractDetail /></ProtectedRoute>} />
                <Route path="/fsm/contracts/:id/edit" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />
                <Route path="/fsm/work-orders" element={<ProtectedRoute permission="fsm_work_orders"><WorkOrderList /></ProtectedRoute>} />
                <Route path="/fsm/work-orders/new" element={<ProtectedRoute><WorkOrderForm /></ProtectedRoute>} />
                <Route path="/fsm/work-orders/:id/edit" element={<ProtectedRoute><WorkOrderForm /></ProtectedRoute>} />
                <Route path="/fsm/work-orders/:id" element={<ProtectedRoute><WorkOrderDetail /></ProtectedRoute>} />
                <Route path="/fsm/price-books" element={<ProtectedRoute><PriceBooks /></ProtectedRoute>} />
                <Route path="/fsm/business-hours" element={<ProtectedRoute><BusinessHours /></ProtectedRoute>} />
                <Route path="/" element={(() => {
                  // Smart redirect: prefer user's default_module, but fall back to
                  // first available page based on actual menu_permissions.
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  const perms = Array.isArray(user.menu_permissions) ? user.menu_permissions : [];
                  const moduleRoutes = { comms: '/dashboard', helpdesk: '/helpdesk', fsm: '/fsm/dashboard' };
                  const moduleKey = { '/dashboard': 'dashboard', '/helpdesk': 'helpdesk', '/fsm/dashboard': 'fsm_dashboard' };
                  const desired = moduleRoutes[user.default_module];
                  // Admin without explicit permissions: legacy fall-through to dashboard
                  if (user.role === 'admin' && !user.menu_permissions) {
                    return <Navigate to={desired || '/dashboard'} replace />;
                  }
                  if (desired && perms.includes(moduleKey[desired])) {
                    return <Navigate to={desired} replace />;
                  }
                  return <Navigate to={getFirstAvailableRoute()} replace />;
                })()} />
                <Route path="/marketing/whatsapp-import" element={<WhatsAppAudienceEditor />} />
                <Route path="/marketing/contacts/:id" element={<ProtectedRoute><MarketingContactList /></ProtectedRoute>} />
                <Route path="/fsm/maintenance-plans" element={<MaintenancePlans />} />
                <Route path="/fsm/preventive-schedule" element={<PreventiveSchedule />} />
                <Route path="/fsm/settings" element={<FSMSettings />} />
                 <Route path="/fsm/contract-templates" element={<ContractTemplates />} />
                 <Route path="/fsm/contract-templates/new" element={<ContractTemplateBuilder />} />
                 <Route path="/fsm/contract-templates/:id/edit" element={<ContractTemplateBuilder />} />
                 <Route path="/fsm/wo-templates" element={<WOTemplates />} />
                 <Route path="/fsm/wo-templates/new" element={<WOTemplateBuilder />} />
                 <Route path="/fsm/wo-templates/:id/edit" element={<WOTemplateBuilder />} />
                 <Route path="/fsm/parts-catalog" element={<PartsCatalog />} />
                <Route path="/fsm/technicians" element={<ProtectedRoute permission="fsm_technicians"><Technicians /></ProtectedRoute>} />
                <Route path="/fsm/dispatch" element={<ProtectedRoute permission="fsm_dispatch"><DispatchBoard /></ProtectedRoute>} />
                <Route path="/fsm/reports" element={<ProtectedRoute permission="fsm_reports"><FSMReports /></ProtectedRoute>} />
                <Route path="/fsm/reports/:id" element={<ProtectedRoute><FSMReportRunner /></ProtectedRoute>} />
                <Route path="/vsm/clients" element={<ProtectedRoute><VSMClients /></ProtectedRoute>} />
                <Route path="/vsm/vehicles" element={<ProtectedRoute><VSMVehicles /></ProtectedRoute>} />
                <Route path="/vsm/service-types" element={<ProtectedRoute><VSMServiceTypes /></ProtectedRoute>} />
                <Route path="/vsm/contracts" element={<ProtectedRoute><VSMContracts /></ProtectedRoute>} />
                <Route path="/vsm/contracts/new" element={<ProtectedRoute><VSMContractForm /></ProtectedRoute>} />
                <Route path="/vsm/contracts/:id" element={<ProtectedRoute><VSMContractDetail /></ProtectedRoute>} />
                <Route path="/vsm/schedule" element={<ProtectedRoute><VSMSchedule /></ProtectedRoute>} />
                <Route path="/vsm/holidays" element={<ProtectedRoute><VSMHolidays /></ProtectedRoute>} />
                <Route path="/vsm/capture" element={<ProtectedRoute><VSMCapture /></ProtectedRoute>} />
              </Routes>
            </div>
          </Router>
        </HelpdeskLanguageProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;